import React, { useState, useEffect } from 'react';
import { 
  X, AlertCircle, CheckCircle, TrendingUp, 
  CreditCard, Lock, Shield, Star, Gift, Loader2
} from 'lucide-react';
import api from '../../api';
import { loadRazorpayScript } from './PaymentCheckoutModal';

const SubscriptionUpgradeModal = ({ 
  isOpen, 
  onClose, 
  currentPlan, 
  selectedPlan, 
  currentPrice,
  onSubmit,
  isSubmitting,
  tenantId,
  currentSubscription
}) => {
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [immediateUpgrade, setImmediateUpgrade] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [, setOrderData] = useState(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadRazorpayScript();
      setError(null);
      setSuccess(false);
      setIsPaymentLoading(false);
      setIsVerifying(false);
      setIsProcessing(false);
      setPaymentCompleted(false);
      setBillingCycle('yearly');
      setImmediateUpgrade(true);
    }
  }, [isOpen, selectedPlan]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const hasActiveSubscription = currentPlan && currentPlan.planId;

  if (!isOpen || !selectedPlan) return null;

  const getAvailableBillingCycles = () => {
    const cycles = [];
    if (selectedPlan?.pricing) {
      if (selectedPlan.pricing.yearly?.isActive && selectedPlan.pricing.yearly?.amount > 0) {
        cycles.push({
          key: 'yearly',
          label: 'Yearly Plan',
          amount: selectedPlan.pricing.yearly.amount,
          description: 'Best total value'
        });
      }
      if (selectedPlan.pricing.quarterly?.isActive && selectedPlan.pricing.quarterly?.amount > 0) {
        cycles.push({
          key: 'quarterly',
          label: 'Quarterly Plan',
          amount: selectedPlan.pricing.quarterly.amount,
          description: 'Standard term'
        });
      }
      if (selectedPlan.pricing.monthly?.isActive && selectedPlan.pricing.monthly?.amount > 0) {
        cycles.push({
          key: 'monthly',
          label: 'Monthly Plan',
          amount: selectedPlan.pricing.monthly.amount,
          description: 'Pay month to month'
        });
      }
    }
    if (cycles.length === 0) {
      cycles.push({
        key: 'yearly',
        label: 'Yearly Plan',
        amount: selectedPlan.pricing?.yearly?.amount || 0,
        description: 'Standard catalog pricing'
      });
    }
    return cycles;
  };

  const availableCycles = getAvailableBillingCycles();
  const selectedCycle = availableCycles.find(c => c.key === billingCycle) || availableCycles[0];

  const taxInfo = selectedPlan?.tax || {};
  const hasTax = taxInfo.taxPercentage > 0;
  const taxName = taxInfo.taxName || 'GST';
  const taxPercentage = taxInfo.taxPercentage || 0;
  const isTaxInclusive = taxInfo.isTaxInclusive || false;

  const isSpecialPrice = selectedPlan?.actualPrice?.isSpecialPrice || false;
  const specialAmount = selectedPlan?.actualPrice?.amount || selectedPlan?.pricing?.yearly?.amount || 0;
  const originalAmount = selectedPlan?.actualPrice?.originalAmount || selectedPlan?.pricing?.yearly?.amount || 0;
  const discountReason = selectedPlan?.actualPrice?.discountReason || '';
  const discountPercentage = selectedPlan?.actualPrice?.discountPercentage || 0;

  const newPlanPrice = isSpecialPrice ? specialAmount : selectedCycle?.amount || 0;

  const calculateTaxAmount = (price) => {
    if (!hasTax || price === 0) return { baseAmount: price, taxAmount: 0, totalAmount: price };
    const taxRate = taxPercentage / 100;
    if (isTaxInclusive) {
      const baseAmount = price / (1 + taxRate);
      const taxAmount = price - baseAmount;
      return { 
        baseAmount: Number(baseAmount.toFixed(2)), 
        taxAmount: Number(taxAmount.toFixed(2)), 
        totalAmount: Number(price.toFixed(2)) 
      };
    } else {
      const taxAmount = price * taxRate;
      return { 
        baseAmount: Number(price.toFixed(2)), 
        taxAmount: Number(taxAmount.toFixed(2)), 
        totalAmount: Number((price + taxAmount).toFixed(2)) 
      };
    }
  };

  const upgradeAmount = hasActiveSubscription ? Math.max(0, newPlanPrice - currentPrice) : newPlanPrice;
  const upgradeTaxDetails = hasActiveSubscription ? calculateTaxAmount(upgradeAmount) : calculateTaxAmount(newPlanPrice);
  const isUpgrade = hasActiveSubscription && newPlanPrice > currentPrice;

  const hasTrial = selectedPlan?.hasTrial || false;
  const trialDays = selectedPlan?.trialDays || 0;

  const isLoading = isSubmitting || isPaymentLoading || isVerifying || isProcessing;
  const showLoadingOverlay = isLoading && !success && !paymentCompleted;

  const verifyPayment = async (razorpayResponse, orderData) => {
    try {
      setIsVerifying(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await api.post('/api/tenant-admin/subscription/payment/verify', {
        razorpayOrderId: razorpayResponse.razorpay_order_id,
        razorpayPaymentId: razorpayResponse.razorpay_payment_id,
        razorpaySignature: razorpayResponse.razorpay_signature,
        paymentId: orderData.paymentId,
        tenantId: tenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPaymentCompleted(true);
        setSuccess(true);
        setIsVerifying(false);
        
        if (onSubmit) {
          try {
            await onSubmit({ 
              action: 'refresh',
              newPlanId: selectedPlan.planId,
              billingCycle: billingCycle,
              immediateUpgrade: immediateUpgrade
            });
          } catch (refreshError) {
            console.warn('Background refresh failed:', refreshError);
          }
        }
        
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setPaymentCompleted(false);
        }, 2500);
      } else {
        setError(response.data.message || 'Payment verification failed.');
        setIsVerifying(false);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || 'Could not verify your payment.');
      setIsVerifying(false);
    }
  };

  const handlePayment = async () => {
    try {
      setIsPaymentLoading(true);
      setError(null);
      const action = hasActiveSubscription ? 'upgrade' : 'new';
      const token = localStorage.getItem('token');
      const response = await api.post('/api/tenant-admin/subscription/payment/create-order', {
        planId: selectedPlan.planId,
        action: action,
        tenantId: tenantId,
        billingCycle: billingCycle,
        immediateUpgrade: immediateUpgrade
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const orderData = response.data.data;
        setOrderData(orderData);
        
        const razorpayOptions = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: Math.round(orderData.amount * 100),
          currency: orderData.currency || 'INR',
          name: 'Truyon',
          description: `${action === 'new' ? 'Setup' : 'Upgrade'}`,
          order_id: orderData.orderId,
          theme: { color: '#4f46e5' },
          modal: {
            ondismiss: function() {
              setIsPaymentLoading(false);
            }
          },
          handler: function(response) {
            verifyPayment(response, orderData);
          }
        };

        const razorpay = new window.Razorpay(razorpayOptions);
        razorpay.open();
      } else {
        setError(response.data.message || 'Could not create checkout token.');
        setIsPaymentLoading(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.message || 'Failed to initialize payment.');
      setIsPaymentLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const amount = hasActiveSubscription ? upgradeAmount : newPlanPrice;
    
    if (amount > 0) {
      await handlePayment();
    } else {
      try {
        setIsProcessing(true);
        const result = await onSubmit({
          newPlanId: selectedPlan.planId,
          billingCycle: billingCycle,
          immediateUpgrade: immediateUpgrade
        });

        if (result?.success) {
          setSuccess(true);
          setTimeout(() => {
            onClose();
            setSuccess(false);
          }, 2500);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to process upgrade.');
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto shadow-2xl relative flex flex-col transition-all border border-slate-100 animate-slideUp sm:animate-scaleIn">
        
        {showLoadingOverlay && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-xs z-50 flex flex-col items-center justify-center p-6 text-center">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
            <h4 className="text-sm font-bold text-slate-800">
              {isVerifying ? 'Verifying your payment...' : isPaymentLoading ? 'Connecting to secure gateway...' : 'Processing your request...'}
            </h4>
            <p className="text-xs text-slate-400 max-w-[200px]">Please wait while we process your request.</p>
          </div>
        )}

        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-sm font-black text-slate-900 flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              {hasActiveSubscription ? 'Upgrade Plan' : 'Subscribe Now'}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[260px]">
              {hasActiveSubscription ? `Changing to ${selectedPlan.name}` : `Setting up ${selectedPlan.name}`}
            </p>
          </div>
          <button 
            type="button"
            onClick={onClose} 
            disabled={isLoading || success}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 flex-1">
          
          <div className="grid grid-cols-2 gap-3 bg-slate-50 border border-slate-200/60 rounded-xl p-3.5 text-xs">
            <div className="space-y-0.5 border-r border-slate-200 pr-1.5">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Current Plan</span>
              <p className="font-bold text-slate-800 truncate">{hasActiveSubscription ? currentPlan?.name : 'No active plan'}</p>
              <p className="text-slate-500 font-medium">{hasActiveSubscription ? `₹${currentPrice.toFixed(2)} / yr` : 'New subscription'}</p>
            </div>
            <div className="space-y-0.5 pl-1.5">
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider block">New Plan</span>
              <p className="font-bold text-slate-900 truncate">{selectedPlan.name}</p>
              <p className="text-emerald-700 font-bold">₹{newPlanPrice.toFixed(2)} / {billingCycle}</p>
            </div>
          </div>

          {isSpecialPrice && (
            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-2 animate-fadeIn">
              <Star className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="text-xs font-bold text-emerald-900 block">Special Price Active</span>
                <p className="text-xs text-emerald-700 leading-normal">{discountReason}</p>
                {discountPercentage > 0 && (
                  <p className="text-xs font-bold text-emerald-800">
                    {discountPercentage}% off original price
                  </p>
                )}
              </div>
            </div>
          )}

          {hasActiveSubscription && isUpgrade && (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 space-y-2 text-xs">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block">Upgrade Details</span>
              <div className="space-y-1.5 text-slate-600 font-medium">
                <div className="flex justify-between">
                  <span>New plan price:</span>
                  <span>₹{newPlanPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Credit from current plan:</span>
                  <span>-₹{currentPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-slate-800 border-t border-slate-200/60 pt-1.5 mt-1">
                  <span>Amount to pay:</span>
                  <span className="text-indigo-600">₹{upgradeAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {hasTrial && (
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl flex items-center gap-2 text-xs text-blue-800 font-medium">
              <Gift className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span>Includes {trialDays} days free trial</span>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide">Select Billing Cycle</label>
            <div className="space-y-2">
              {availableCycles.map((cycle) => (
                <button
                  key={cycle.key}
                  type="button"
                  onClick={() => !isLoading && !success && setBillingCycle(cycle.key)}
                  disabled={isLoading || success}
                  className={`w-full p-3 text-left rounded-xl border flex items-center justify-between transition-all ${
                    billingCycle === cycle.key
                      ? 'border-indigo-600 bg-indigo-50/50 ring-2 ring-indigo-100'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-slate-900 block">{cycle.label}</span>
                    <span className="text-[11px] text-slate-400 block">{cycle.description}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-black text-slate-900 block">₹{cycle.amount.toFixed(2)}</span>
                    {hasTax && <span className="text-[10px] text-slate-400 block">+ {taxPercentage}% {taxName}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {hasActiveSubscription && (
            <div className="p-1">
              <label className="flex items-start gap-2 text-xs text-slate-600 font-medium cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={immediateUpgrade}
                  onChange={(e) => !isLoading && !success && setImmediateUpgrade(e.target.checked)}
                  disabled={isLoading || success}
                  className="w-3.5 h-3.5 mt-0.5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                />
                <div className="space-y-0.5">
                  <span className="font-bold text-slate-800 block">Apply changes immediately</span>
                  <p className="text-[11px] text-slate-400 leading-normal">
                    {immediateUpgrade 
                      ? 'Plan limits and features will be updated today.' 
                      : 'Changes will apply at the end of current billing period.'}
                  </p>
                </div>
              </label>
            </div>
          )}

          {hasTax && (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 space-y-1.5 text-xs text-slate-500 font-medium">
              <div className="flex justify-between">
                <span>Base price:</span>
                <span>₹{upgradeTaxDetails.baseAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>{taxName} ({taxPercentage}%):</span>
                <span>₹{upgradeTaxDetails.taxAmount.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-slate-400 italic pt-1 border-t border-slate-200/40">
                {isTaxInclusive ? '*Tax included in price' : '*Tax added at checkout'}
              </p>
            </div>
          )}

          <div className="bg-indigo-600 text-white rounded-xl p-4 shadow-md space-y-0.5">
            <div className="flex justify-between items-center">
              <span className="text-xs font-semibold opacity-90">Total Amount</span>
              <span className="text-lg font-black">₹{upgradeTaxDetails.totalAmount.toFixed(2)}</span>
            </div>
            {isSpecialPrice && hasActiveSubscription && (
              <p className="text-[10px] opacity-80 font-medium">
                Savings: ₹{(originalAmount - specialAmount).toFixed(2)} ({Math.round(discountPercentage)}% off)
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-2 animate-fadeIn">
              <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-rose-700 font-medium leading-normal">{error}</p>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="text-xs text-rose-600 font-semibold hover:text-rose-700 mt-1"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {success && (
            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-2 animate-fadeIn">
              <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-emerald-700 leading-normal font-bold">
                  {hasActiveSubscription ? 'Plan upgraded successfully!' : 'Subscription activated successfully!'}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  Closing...
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row items-center gap-2 pt-2 border-t border-slate-50">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading && !success}
              className="w-full sm:w-1/3 py-2.5 text-center text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || success}
              className="w-full sm:w-2/3 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition shadow-sm active:scale-98 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <CreditCard className="w-3.5 h-3.5" />
              <span>
                {hasActiveSubscription 
                  ? (upgradeAmount > 0 ? 'Pay & Upgrade' : 'Confirm Upgrade') 
                  : 'Subscribe Now'}
              </span>
            </button>
          </div>

          <div className="flex items-center justify-center gap-3 text-[10px] text-slate-400 font-medium pt-1">
            <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-slate-300" /> Secure Checkout</span>
            <span className="text-slate-200">|</span>
            <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-slate-300" /> Encrypted by Razorpay</span>
          </div>

        </form>
      </div>
    </div>
  );
};

export default SubscriptionUpgradeModal;